import React, { Component } from 'react';
import Slider from './Slider';

const totalRenderedSlides = 3;
const slidesArray = Array.apply(null, Array(totalRenderedSlides));
const slideSize = 100 / totalRenderedSlides;
const animationSpeed = 0.25;

type Props = {
  renderSlide: (key: number) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
  onChange: (newSlideIndex: number) => void;
};

class SliderContainer extends Component<Props> {
  state = {
    percentageX: -slideSize,
    transition: 0,
    easing: 'ease-out',
    isPanEnabled: true,
  };

  slideDirection = 0;
  sliderRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.trackTransitionEnd();
  }

  trackTransitionEnd = () => {
    this.sliderRef?.current?.addEventListener('transitionend', e => {
      const isRefEl = e.target === this.sliderRef.current;
      if (isRefEl && e.propertyName === 'transform') {
        this.reShuffleSlides();
      }
    });
  };

  reShuffleSlides = () => {
    this.setState({
      percentageX: -slideSize,
      transition: 0,
      isPanEnabled: true,
    });

    this.props.onChange(this.slideDirection);
  };

  calcSlideDirection = (e: HammerInput) => {
    // Get new slide based on direction of touch, velolcity and deltaX
    const absoluteDeltaX = Math.abs(e.deltaX);
    const absoluteVelocity = Math.abs(e.velocityX);
    const percentageMoved = (absoluteDeltaX / window.innerWidth) * 100;
    const isEnoughSpeedOrDistance =
      percentageMoved > 50 || absoluteVelocity > 0.2;

    let direction = 0;
    if (isEnoughSpeedOrDistance) {
      // Direction based on deltaX (direction finger moved) and velocity (flick at the end)
      // Positive delta and small negative velocity allows a slight flick in the wrong direction
      // still allowing slide to change
      const prev = e.velocityX >= -0.4 && e.deltaX >= 0;
      const next = e.velocityX <= 0.4 && e.deltaX <= 0;
      if (prev) direction = -1;
      if (next) direction = 1;
    }

    this.slideDirection = direction;
    return this.slideDirection;
  };

  snapToSlide = (e: HammerInput, slide: number) => {
    const targetPercentage = slideSize * (slide - 1);
    const absoluteVelocity = Math.abs(e.velocityX);

    // Ease out if low velocity (swipe speed)
    const easing = absoluteVelocity < 0.3 ? 'ease-in-out' : 'ease-out';

    this.setState({
      transition: animationSpeed,
      percentageX: -targetPercentage,
      easing,
      isPanEnabled: false,
    });
  };

  handlePan = (e: HammerInput) => {
    // Move slider along with touch position
    const startPercentage = slideSize;
    const percentageMoved = (slideSize * e.deltaX) / window.innerWidth;
    const percentageX = percentageMoved - startPercentage;

    this.setState({ percentageX, transition: 0 });

    // Animate to next slide
    if (e.isFinal) {
      const slideDirection = this.calcSlideDirection(e);
      this.snapToSlide(e, 2 + slideDirection);
    }
  };

  generateSlideStyle = () => {
    const { percentageX, transition, easing } = this.state;
    return {
      transform: `translate3d(${percentageX}%, 0, 0)`,
      transitionDuration: `${transition}s`,
      transitionTimingFunction: easing,
    };
  };

  generateProps = () => ({
    ...this.state,
    ...this.props,
    slideIndex: 0,
    slideStyle: this.generateSlideStyle(),
    handlePan: this.handlePan,
    sliderRef: this.sliderRef,
    slidesArray,
  });

  render() {
    const props = this.generateProps();
    return <Slider {...props} />;
  }
}

export default SliderContainer;
